import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/app';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ProfileEditProps {
  user: any;
  token: string;
  onClose: () => void;
}

const ProfileEdit: React.FC<ProfileEditProps> = ({ user, token, onClose }) => {
  const { language } = useLanguage();
  const L = (language === 'en' ? enStrings : elStrings).profileEdit;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(L.errorRequired);
      return;
    }
    if (newPassword.length < 6) {
      setError(L.errorMinLength);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(L.errorMismatch);
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/users/${user.id}/password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(L.successMsg);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || L.errorGeneric;
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Admin', pm: 'Project Manager',
      coordinator: 'Project Coordinator', guest_user: 'Guest User',
    };
    return labels[role] ?? role;
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>{L.title}</h2>
            <p style={styles.modalSubtitle}>{user.full_name} · {user.email}</p>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={styles.infoBox}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{L.roleLabel}</span>
            <span style={styles.roleBadge}>{getRoleLabel(user.role)}</span>
          </div>
        </div>
        <div style={styles.divider} />
        <h3 style={styles.sectionTitle}>{L.sectionTitle}</h3>
        <div style={styles.field}>
          <label style={styles.label}>{L.currentPassword}</label>
          <input style={styles.input} type="password" value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>{L.newPassword}</label>
          <input style={styles.input} type="password" value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)} placeholder={L.newPasswordPlaceholder} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>{L.confirmPassword}</label>
          <input style={styles.input} type="password" value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
        </div>
        {error && <p style={styles.error}>{error}</p>}
        {success && <p style={styles.success}>{success}</p>}
        <div style={styles.modalButtons}>
          <button style={styles.cancelBtn} onClick={onClose}>{L.close}</button>
          <button style={styles.saveBtn} onClick={handleChangePassword} disabled={saving}>
            {saving ? L.saving : L.changeBtn}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '460px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  modalSubtitle: { fontSize: '13px', color: '#9ca3af', margin: '4px 0 0 0' },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#9ca3af', padding: '4px' },
  infoBox: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
  infoRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  infoLabel: { fontSize: '13px', color: '#6b7280' },
  roleBadge: { backgroundColor: '#ede9fe', color: '#4f46e5', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  divider: { borderTop: '1px solid #e5e7eb', margin: '20px 0' },
  sectionTitle: { fontSize: '15px', fontWeight: '600', color: '#1a1a2e', marginBottom: '16px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333', fontSize: '13px' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' as const, outline: 'none', fontFamily: 'inherit' },
  error: { color: '#ef4444', fontSize: '13px', marginBottom: '12px' },
  success: { color: '#10b981', fontSize: '13px', marginBottom: '12px' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default ProfileEdit;
